import { Box, Typography, Divider, Stack } from "@mui/material";

export const HoukPaper = () => {
  return (
    <Box
      sx={{
        width: "100%",
        py: { xs: 4, sm: 5, md: 6 },
        mt: { xs: 4, sm: 6, md: 8 },
        backgroundColor: "#F5F5F5",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color="#1B2E85"
        textAlign="center"
        sx={{
          fontSize: { xs: "1.8rem", sm: "2rem", md: "2.3rem" },
        }}
      >
        How AI Is Transforming Cancer Literacy
      </Typography>

      <Typography
        variant="subtitle1"
        color="text.secondary"
        textAlign="center"
        sx={{
          mb: 4,
          fontWeight: "bold",
          fontSize: { xs: "1rem", sm: "1.15rem", md: "1.3rem" },
        }}
      >
        ChatGPT + Human Review: Faster, Clearer Cancer Communication
      </Typography>

      <Divider
        sx={{
          width: { xs: "40px", sm: "50px", md: "60px" },
          height: { xs: "2px", sm: "3px" },
          backgroundColor: "#1B2E85",
          margin: "16px auto",
          borderRadius: "2px",
          mb: 4,
        }}
      />

      {/* Visual Abstract */}
      <Box
        sx={{
          maxWidth: { xs: "95%", sm: "800px", md: "950px", lg: "1050px" },
          mx: "auto",
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/cancergeneticsAI/visual_abstract.jpg"
          alt="AI in Patient Communication of Hereditary Cancer Syndromes"
          sx={{
            width: "100%",
            height: "auto",
            borderRadius: 2,
            boxShadow: 2,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mt: 1,
            color: "text.secondary",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
          }}
        >
          ChatGPT-assisted summaries, reviewed by clinicians, reduced creation
          time while maintaining accuracy; translation support can expand
          access.
        </Typography>
      </Box>

      {/* Author’s Note */}
      <Box sx={{ maxWidth: { xs: "95%", sm: 900 }, mx: "auto", mb: 6 }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: "white",
            border: "1px solid #E6E6E6",
            borderRadius: 2,
            borderLeft: "4px solid #1B2E85",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#1B2E85",
              mb: 1,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            From Emalie Houk, Co-Author of{" "}
            <i>ChatGPT + Human Review: Faster, Clearer Cancer Communication</i>
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontStyle: "italic",
              lineHeight: 1.7,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Hereditary cancer syndromes present significant challenges in both
            education and communication due to the complexity and vastness of
            the information involved. Our team explored how artificial
            intelligence (AI), specifically ChatGPT, can help make this
            information easier to understand and access. We used AI to create
            clear, easy-to-read summaries about different hereditary cancer
            syndromes and then reviewed them for accuracy. The process of
            creating summaries with ChatGPT saved a lot of time and helped us
            create helpful resources for patients more quickly. Although
            efficient, the AI-generated summaries occasionally contained errors,
            highlighting the importance of human oversight. Our study
            demonstrates the promise of AI to improve health education and
            improve patient-clinician communication. The integration of AI into
            medical education offers significant opportunities to improve health
            literacy and empower both patients and healthcare professionals.
          </Typography>
        </Box>
      </Box>

      {/* Two text blocks side-by-side */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        sx={{ maxWidth: "1000px", mx: "auto", mb: 6 }}
      >
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3, md: 4 },
            backgroundColor: "white",
            border: "1px solid #E6E6E6",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              color: "#1B2E85",
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Methods Snapshot
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.8, fontSize: { xs: "0.9rem", sm: "1rem" } }}
            component="div"
          >
            <ol style={{ margin: 0, paddingLeft: "1.2em" }}>
              <li>Identify target hereditary cancer syndromes</li>
              <li>Use ChatGPT to generate patient-friendly summaries</li>
              <li>Review by subject-matter experts</li>
              <li>Revise and publish for patient access</li>
            </ol>
          </Typography>
        </Box>

        {/* Prompt Template */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            backgroundColor: "white",
            border: "1px solid #E6E6E6",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              color: "#1B2E85",
              mb: 1,
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Prompt Template Used
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              lineHeight: 1.5,
              backgroundColor: "#FAFAFA",
              border: "1px solid #EAEAEA",
              borderRadius: 1,
              p: 2,
              color: "text.secondary",
            }}
          >
            {`You are a geneticist with an expertise in cancer genetics. Give an MD reading level summary of ____[disorder] including alternate names for the syndrome (as a list), genes involved (as a list), organs affected (as a list), who discovered it, first references from the people who discovered the syndrome, years it was first described, recent references, and incidence in population, screening and intervention strategies, and FAQs with section headings in bold, references at the end with footnote markers in the text and hyperlinks for the references.`}
          </Box>
        </Box>
      </Stack>

      {/* Read the Full Paper CTA */}
      <Box
        sx={{
          maxWidth: "1000px",
          mx: "auto",
          textAlign: "center",
          backgroundColor: "white",
          border: "1px solid #E6E6E6",
          borderRadius: 2,
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            mb: 0.5,
            color: "#1B2E85",
            textAlign: "center",
            fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.1rem" },
          }}
        >
          Interested in learning more?
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            textAlign: "center",
            fontSize: { xs: "0.9rem", sm: "1rem" },
          }}
        >
          Read the full paper by <strong>Emalie Houk B.S.</strong>,{" "}
          <strong>Kiersten Meeder M.S.</strong>,{" "}
          <strong>Paloma Gallagher B.S.</strong>, and{" "}
          <strong>Kevin Hughes M.D.</strong>
        </Typography>

        <a
          href="/cancergeneticsAI/publication.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Typography
            sx={{
              display: "inline-block",
              color: "#1B2E85",
              fontWeight: "bold",
              borderBottom: "2px solid transparent",
              transition: "border-color 0.2s ease",
              "&:hover": {
                borderBottom: "2px solid #1B2E85",
              },
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Read Full Paper →
          </Typography>
        </a>
      </Box>
    </Box>
  );
};
